import { FormGroup } from '@angular/forms';

/**
 * Vérifie si un objet contient une propriété spécifique et si cette propriété est d'un type donné.
 * @param obj L'objet à vérifier.
 * @param key La clé de la propriété à vérifier.
 * @param typeGuard (Optionnel) Une fonction de garde de type pour vérifier le type de la propriété.
 * @returns `true` si la propriété existe et (optionnellement) est du type attendu, sinon `false`.
 */
export function hasProperty<T extends object, K extends keyof any>(
  obj: T,
  key: K,
  typeGuard?: (value: unknown) => boolean
): obj is T & Record<K, unknown> {
  const hasKey = key in obj;
  if (!hasKey) return false;

  const value = (obj as Record<K, unknown>)[key];

  if (typeGuard) {
    return typeGuard(value);
  }

  return true;
}

/**
 * Récupère une propriété d'un objet de manière sécurisée
 * @param obj L'objet source
 * @param key La clé de la propriété
 * @returns La valeur de la propriété
 */
export function getProperty<T, K extends keyof T>(obj: T, key: K): T[K] {
  return obj[key];
}

/**
 * Met à jour une propriété d'un objet de manière sécurisée
 * @param obj L'objet à mettre à jour
 * @param key La clé de la propriété
 * @param value La nouvelle valeur
 */
export function updateProperty<T, K extends keyof T>(
  obj: T,
  key: K,
  value: T[K]
): void {
  obj[key] = value;
}

/**
 * Affiche les erreurs d'un formulaire dans la console
 * @param formGroup Le FormGroup à analyser
 */
export function logFormErrors(formGroup: FormGroup): void {
  Object.keys(formGroup.controls).forEach((key) => {
    const control = formGroup.get(key);
    if (control instanceof FormGroup) {
      logFormErrors(control);
    } else if (control?.errors) {
      console.log(`Erreurs pour le champ ${key}:`, control.errors);
    }
  });
}

/**
 * Convertit une chaîne de temps (HH:MM) en minutes
 * @param time La chaîne de temps à convertir
 * @returns Le nombre total de minutes
 */
export function convertToMinutes(time: string): number {
  if (typeof time !== 'string') {
    return time;
  }

  if (!time?.includes(':')) return 0;

  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return 0;

  return hours * 60 + minutes;
}

/**
 * Convertit des minutes en chaîne de temps (HH:MM)
 * @param totalMinutes Le nombre total de minutes
 * @returns La chaîne de temps formatée
 */
export function convertMinutesToTime(totalMinutes: number): string {
  if (isNaN(totalMinutes) || totalMinutes < 0) return '00:00';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}`;
}

/**
 * Extrait les IDs des URLs fournies
 * @param urls Tableau d'URLs
 * @returns Tableau d'IDs extraits
 */
export function extractIdsFromElementsUrls(urls: string[]): string[] {
  return urls
    .map((url) => {
      const match = url.match(/\/([^\/]+)\/?$/);
      return match ? match[1] : '';
    })
    .filter((id): id is string => id !== '');
}

/**
 * Extrait le dernier segment d'une URL (ou l'identifiant si c'est déjà un nombre/string simple)
 * @param url L'URL ou l'identifiant à analyser (string ou number)
 * @returns Le dernier segment de l'URL ou la valeur convertie en string
 */
export function extractLastSegmentFromUrl(url: string | number | null | undefined): string {
  if (url == null) return '';
  const s = String(url);
  return s.split('/').pop() || s;
}

/**
 * Remplace les identifiants dans un tableau d'objets
 * @param items Tableau d'objets à modifier
 * @param referenceList Tableau de référence
 * @param keyToReplace Clé à remplacer
 * @param referenceKey Clé de référence
 * @returns Nouveau tableau avec identifiants remplacés
 */
export function replaceIdentifiers<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  K extends keyof T,
  L extends keyof U
>(
  items: T[],
  referenceList: U[],
  keyToReplace: K,
  referenceKey: L
): (T & Record<K, U | T[K]>)[] {
  return items.map((item) => {
    const itemValue = item[keyToReplace];
    const matchedReference = referenceList.find(
      (ref) => String(ref[referenceKey]) === String(itemValue)
    );

    return {
      ...item,
      [keyToReplace]: matchedReference ?? item[keyToReplace],
    } as T & Record<K, U | T[K]>;
  });
}

/**
 * Remplace les identifiants dans un tableau contenu dans un objet
 * @param items Tableau d'objets à modifier
 * @param referenceList Tableau de référence
 * @param arrayKeyToReplace Clé du tableau à remplacer
 * @param referenceKey Clé de référence
 * @returns Nouveau tableau avec identifiants remplacés
 */
export function replaceArrayIdentifiers<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  K extends keyof T,
  L extends keyof U
>(items: T[], referenceList: U[], arrayKeyToReplace: K, referenceKey: L): T[] {
  return items.map((item) => ({
    ...item,
    [arrayKeyToReplace]: (item[arrayKeyToReplace] as unknown[]).map(
      (id) =>
        referenceList.find((ref) => String(ref[referenceKey]) === String(id)) ??
        id
    ),
  }));
}

/**
 * Filtre les tableaux d'objets
 * @param objectLists Tableau potentiel d'objets
 * @returns Tableau d'objets filtré
 */
export function getObjectLists<T>(objectLists: T[] | string[]): T[] {
  return objectLists.length === 0 || typeof objectLists[0] === 'string'
    ? []
    : (objectLists as T[]);
}

/**
 * Génère une chaîne aléatoire
 * @param length Longueur de la chaîne
 * @returns Chaîne aléatoire
 */
export function generateRandomString(length: number): string {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length))
  ).join('');
}

/**
 * Calcule le montant total avec TVA et remise
 * @param quantity Quantité
 * @param price Prix unitaire
 * @param vat Taux de TVA (en pourcentage)
 * @param discount Montant de la remise
 * @param discountType Type de remise ('PERCENT' ou 'AMOUNT')
 * @returns Montant total formaté avec 2 décimales
 */
export function getTotalAmount(
  quantity: number,
  price: number,
  vat: number,
  discount: number = 0,
  discountType: 'PERCENT' | 'AMOUNT' = 'PERCENT'
): string {
  let amountExclTax = quantity * price;

  amountExclTax -=
    discountType === 'PERCENT' ? amountExclTax * (discount / 100) : discount;

  const vatAmount = amountExclTax * (vat / 100);
  const totalAmount = amountExclTax + vatAmount;

  return totalAmount.toFixed(2);
}

export function formatSiret(input: string): string {
  // Supprimer tous les espaces du string
  if (!input) return input;
  const stringWithoutSpaces = input.replace(/\s/g, '');

  // Insérer un espace entre chaque modulo 3
  const stringWithSpaces = stringWithoutSpaces.replace(/(.{3})/g, '$1 ');

  // Transformer la chaîne en tableau en utilisant les espaces comme séparateurs
  let stringArray = stringWithSpaces.trim().split(' ');

  // Vérifier la longueur du dernier élément du tableau
  const lastElement = stringArray[stringArray.length - 1];
  if (lastElement.length < 3) {
    // Supprimer le dernier élément
    stringArray.pop();
    // Ajouter le contenu du dernier élément dans l'avant-dernier élément
    stringArray[stringArray.length - 1] += lastElement;
  }

  // Retourner une chaîne de caractères en joignant les éléments du tableau avec des espaces
  return stringArray.join(' ');
}

export function copyToClipboard(variableKey: string) {
  // Copie du texte de la variable dans le presse-papiers
  navigator.clipboard
    .writeText(variableKey)
    .then(() => {})
    .catch((err) => {});
  return false;
}


export function getPriorityColor(priority: number | undefined): string {
  switch (priority) {
    case 2:
      return '#dc3545'; // High
    case 1:
      return '#fd7e14'; // Medium
    case 0:
      return '#198754'; // Low
    default:
      return '#6c757d'; // Default
  }
}

/**
 * Formate un nom d'utilisateur selon le format : Prénom (première lettre majuscule) + deux premières lettres du nom en majuscules
 * Exemple: "atta Fall" devient "Atta FA"
 * @param firstName Le prénom de l'utilisateur
 * @param lastName Le nom de famille de l'utilisateur
 * @returns Le nom formaté selon le format demandé
 */
export function formatUserName(firstName: string, lastName: string): string {
  if (!firstName || !lastName) return '';
  
  // Prénom avec première lettre en majuscule et le reste en minuscules
  const formattedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  
  // Deux premières lettres du nom en majuscules
  const formattedLastName = lastName.substring(0, 2).toUpperCase();
  
  return `${formattedFirstName} ${formattedLastName}`;
}
